import { useEffect } from 'react'
import {
    exitTelegramFullscreen,
    expandTelegramApp,
    initializeTelegramApp,
    requestTelegramFullscreen,
    useTelegramWebApp,
} from '@/types/telegram'
import { appLogger } from '@/utils/logger'
import { appConfig } from '@/config/app'
import { useAppStore } from '@/store/appStore'
import {
    applySafeAreaCssVariables,
    resolveTelegramDeviceSafeAreaInsets,
    resolveTelegramSafeAreaInsets,
} from '@/utils/safeArea'

import { hasTelegramInitData } from '@/utils/runtime'

const appColors = appConfig.ui.colors

export const useTelegramInit = () => {
    const webApp = useTelegramWebApp()
    const fetchMiniAppSettings = useAppStore((state) => state.fetchMiniAppSettings)
    const fullscreenMode = useAppStore((state) => state.miniAppSettings?.fullscreenMode ?? 'enabled')

    useEffect(() => {
        void fetchMiniAppSettings()

        if (!hasTelegramInitData() || !window.Telegram?.WebApp) {
            return undefined
        }

        initializeTelegramApp()

        const tgWebApp = window.Telegram.WebApp

        const syncSafeArea = () => {
            applySafeAreaCssVariables({
                contentInsets: resolveTelegramSafeAreaInsets(tgWebApp),
                deviceInsets: resolveTelegramDeviceSafeAreaInsets(tgWebApp),
            })
        }

        syncSafeArea()

        tgWebApp.onEvent?.('safeAreaChanged', syncSafeArea)
        tgWebApp.onEvent?.('contentSafeAreaChanged', syncSafeArea)

        // Предотвращаем конфликт свайпа модалки со свайпом закрытия Mini App в Telegram
        try {
            if (typeof tgWebApp.disableVerticalSwipes === 'function') {
                tgWebApp.disableVerticalSwipes()
            }
        } catch (err) {
            if (import.meta.env.DEV) {
                appLogger.warn('disableVerticalSwipes не поддерживается в этом клиенте Telegram', err)
            }
        }

        // Попытка установить через setHeader если доступно
        try {
            tgWebApp.setHeader?.({
                header_color: appColors.pageBgHex.slice(1),
                text_color: appColors.textPrimaryHex.slice(1),
            })
        } catch {
            // no-op: CSS variables fallback is applied below
        }
        // Установка через CSS переменные для гарантированной поддержки
        document.documentElement.style.setProperty('--tg-color-bg', appColors.pageBgHex)
        document.documentElement.style.setProperty('--tg-color-text', appColors.textPrimaryHex)
        // Также устанавливаем прямые свойства если они существуют
        if (tgWebApp.headerColor !== undefined) {
            tgWebApp.headerColor = appColors.pageBgHex
        }

        // === ОГРАНИЧЕНИЕ ОРИЕНТАЦИИ: только портрет ===
        try {
            if (typeof tgWebApp.lockOrientation === 'function') {
                tgWebApp.lockOrientation('portrait')
            } else {
                if (import.meta.env.DEV) {
                    appLogger.warn('lockOrientation не поддерживается в этом клиенте Telegram')
                }
            }
        } catch (err) {
            if (import.meta.env.DEV) {
                appLogger.error('Ошибка при попытке заблокировать ориентацию', err)
            }
        }

        return () => {
            tgWebApp.offEvent?.('safeAreaChanged', syncSafeArea)
            tgWebApp.offEvent?.('contentSafeAreaChanged', syncSafeArea)
        }
    }, [fetchMiniAppSettings])

    useEffect(() => {
        if (!fullscreenMode || !window.Telegram?.WebApp) {
            return
        }

        const tgWebApp = window.Telegram.WebApp
        let retryTimer: number | null = null
        let retryAttempts = 0
        const maxRetryAttempts = 8
        const retryDelayMs = 180

        if (
            typeof tgWebApp.isVersionAtLeast === 'function' &&
            !tgWebApp.isVersionAtLeast(appConfig.telegram.fullscreenMinVersion)
        ) {
            if (import.meta.env.DEV) {
                appLogger.warn(`Fullscreen API требует Telegram >= ${appConfig.telegram.fullscreenMinVersion}`)
            }
            return
        }

        const syncViewportMode = () => {
            if (fullscreenMode === 'enabled') {
                expandTelegramApp(tgWebApp)

                if (!tgWebApp.isFullscreen) {
                    requestTelegramFullscreen(tgWebApp)
                }

                return
            }

            if (tgWebApp.isFullscreen) {
                exitTelegramFullscreen(tgWebApp)
            }
        }

        const clearRetryTimer = () => {
            if (retryTimer !== null) {
                window.clearTimeout(retryTimer)
                retryTimer = null
            }
        }

        const scheduleRetry = () => {
            if (fullscreenMode !== 'enabled') {
                return
            }

            if (tgWebApp.isExpanded && tgWebApp.isFullscreen) {
                clearRetryTimer()
                return
            }

            if (retryAttempts >= maxRetryAttempts) {
                clearRetryTimer()
                return
            }

            clearRetryTimer()
            retryTimer = window.setTimeout(() => {
                retryAttempts += 1
                syncViewportMode()
                scheduleRetry()
            }, retryDelayMs)
        }

        const handleFullscreenChanged = () => {
            if (import.meta.env.DEV) {
                appLogger.info('Telegram fullscreen status changed', { isFullscreen: tgWebApp.isFullscreen })
            }

            scheduleRetry()
        }

        const handleViewportChanged = () => {
            if (fullscreenMode === 'enabled' && (!tgWebApp.isExpanded || !tgWebApp.isFullscreen)) {
                syncViewportMode()
                scheduleRetry()
            }
        }

        const handleVisibilityChanged = () => {
            if (fullscreenMode === 'enabled') {
                syncViewportMode()
                scheduleRetry()
            }
        }

        const handleFullscreenFailed = () => {
            if (import.meta.env.DEV) {
                appLogger.warn('Telegram сообщил ошибку переключения fullscreen')
            }

            scheduleRetry()
        }

        tgWebApp.onEvent?.('fullscreenChanged', handleFullscreenChanged)
        tgWebApp.onEvent?.('fullscreenFailed', handleFullscreenFailed)
        tgWebApp.onEvent?.('viewportChanged', handleViewportChanged)
        tgWebApp.onEvent?.('visibilityChanged', handleVisibilityChanged)

        try {
            syncViewportMode()
            scheduleRetry()
        } catch (err) {
            if (import.meta.env.DEV) {
                appLogger.warn('Не удалось применить fullscreen режим Mini App', err)
            }
        }

        return () => {
            clearRetryTimer()
            tgWebApp.offEvent?.('fullscreenChanged', handleFullscreenChanged)
            tgWebApp.offEvent?.('fullscreenFailed', handleFullscreenFailed)
            tgWebApp.offEvent?.('viewportChanged', handleViewportChanged)
            tgWebApp.offEvent?.('visibilityChanged', handleVisibilityChanged)
        }
    }, [fullscreenMode])

    return webApp
}
